import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "mb-3" }
const _hoisted_2 = ["for"]
const _hoisted_3 = { class: "input-group" }
const _hoisted_4 = ["id", "name", "value", "readonly", "onInput", "onBlur"]
const _hoisted_5 = { class: "input-group-text" }
const _hoisted_6 = {
  key: 0,
  class: "alert alert-secondary small"
}
const _hoisted_7 = { class: "row" }
const _hoisted_8 = { class: "col-3" }
const _hoisted_9 = { class: "mb-3" }
const _hoisted_10 = ["for"]
const _hoisted_11 = ["id", "value", "disabled", "onInput"]
const _hoisted_12 = { class: "col" }
const _hoisted_13 = { class: "mb-3" }
const _hoisted_14 = ["for"]
const _hoisted_15 = {
  key: 0,
  class: "text-danger"
}
const _hoisted_16 = ["id", "value", "disabled", "onInput"]
const _hoisted_17 = { class: "col" }
const _hoisted_18 = { class: "mb-3" }
const _hoisted_19 = ["for"]
const _hoisted_20 = {
  key: 0,
  class: "text-danger"
}
const _hoisted_21 = ["id", "value", "disabled", "onInput"]
const _hoisted_22 = {
  key: 1,
  class: "mb-3"
}
const _hoisted_23 = ["value"]
const _hoisted_24 = { class: "mb-3" }
const _hoisted_25 = ["for"]
const _hoisted_26 = ["id", "value", "disabled", "onInput", "onBlur"]
const _hoisted_27 = { class: "mb-3" }
const _hoisted_28 = ["for"]
const _hoisted_29 = ["id", "value", "onInput", "onBlur"]
const _hoisted_30 = { class: "form-text text-muted" }
const _hoisted_31 = { class: "mb-1" }
const _hoisted_32 = {
  key: 0,
  class: "alert alert-info mt-3",
  "data-testid": "generated-password"
}
const _hoisted_33 = {
  key: 1,
  class: "alert alert-danger mt-3"
}
const _hoisted_34 = ["disabled"]

import { ref, computed } from "vue";
import { useForm } from "@tanstack/vue-form";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import BaseModal from "./BaseModal.vue";
import FieldErrors from "./FieldErrors.vue";
import RoleMultiSelect from "./RoleMultiSelect.vue";
import {
	LOCAL_DOMAIN,
	WWU_DOMAIN,
	useCreateLocalAccountMutation,
	useCreateWwuAccountMutation,
	useUpdateUserMutation,
} from "../api.js";


export default {
  __name: 'UserFormModal',
  props: {
	show: { type: Boolean, default: false },
	mode: { type: String, required: true }, // 'create-local' | 'create-wwu' | 'edit'
	user: { type: Object, default: null },
	roles: { type: Array, default: () => [] },
},
  emits: ["close", "saved"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isCreateWwu = computed(() => props.mode === "create-wwu");
const isEdit = computed(() => props.mode === "edit");
const showIdentityFields = computed(() => !isCreateWwu.value);
const identityFieldsReadonly = computed(() => isEdit.value && props.user?.isLdapAccount === true);
const accountReadonly = computed(() => isEdit.value);
const domainSuffix = computed(
	() => `@${isCreateWwu.value || identityFieldsReadonly.value ? WWU_DOMAIN : LOCAL_DOMAIN}`
);

const modalTitle = computed(() => {
	if (props.mode === "create-local") return "Lokalen Account anlegen";
	if (props.mode === "create-wwu") return "Uni-Account importieren";
	return "Account bearbeiten";
});

const selectedRoleIds = ref((props.user?.roles || []).map((role) => role.id));

const generatedPassword = ref(null);
const submitError = ref(null);

// Laminas\InputFilter::getMessages() errors, mapped by field name. Kept as
// plain component state rather than TanStack Form's own field meta, since
// that gets recomputed by the form's submit lifecycle and does not reliably
// hold externally-injected (server-side) errors across a re-render.
const serverErrors = ref({});
const fieldErrors = (name) => serverErrors.value[name] || [];
const handleFieldInput = (field, value) => {
	field.handleChange(value);
	if (serverErrors.value[field.name]) {
		const next = { ...serverErrors.value };
		delete next[field.name];
		serverErrors.value = next;
	}
};

const queryClient = useQueryClient();
const createLocalMutation = useMutation(useCreateLocalAccountMutation(queryClient));
const createWwuMutation = useMutation(useCreateWwuAccountMutation(queryClient));
const updateMutation = useMutation(useUpdateUserMutation(queryClient));

const isSaving = computed(
	() => createLocalMutation.isPending.value || createWwuMutation.isPending.value || updateMutation.isPending.value
);

const form = useForm({
	defaultValues: {
		id: props.user?.id ?? null,
		account: props.user?.account ?? "",
		title: props.user?.title ?? "",
		firstname: props.user?.firstname ?? "",
		lastname: props.user?.lastname ?? "",
		mail: props.user?.mail ?? "",
		password: "",
	},
	onSubmit: async ({ value }) => {
		submitError.value = null;
		generatedPassword.value = null;
		serverErrors.value = {};

		const payload = { ...value, roles: selectedRoleIds.value };

		try {
			let result;
			if (props.mode === "create-local") {
				result = await createLocalMutation.mutateAsync(payload);
				generatedPassword.value = result.generatedPassword || null;
			} else if (props.mode === "create-wwu") {
				result = await createWwuMutation.mutateAsync({ account: payload.account, roles: payload.roles });
			} else {
				result = await updateMutation.mutateAsync(payload);
			}
			emit("saved", result.user);
			if (!generatedPassword.value) {
				emit("close");
			}
		} catch (error) {
			if (error.invalid && error.messages) {
				const mapped = {};
				Object.entries(error.messages).forEach(([field, fieldMessages]) => {
					mapped[field] = Object.values(fieldMessages);
				});
				serverErrors.value = mapped;
			} else {
				submitError.value = error.message;
			}
		}
	},
});

const computedDisplayName = computed(() => {
	const values = form.state.values;
	return [values.title, values.firstname, values.lastname].filter(Boolean).join(" ");
});

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(BaseModal, {
    show: __props.show,
    title: modalTitle.value,
    size: "lg",
    "modal-id": "userFormModal",
    onClose: _cache[3] || (_cache[3] = $event => (_ctx.$emit('close')))
  }, {
    body: _withCtx(() => [
      _createElementVNode("form", {
        id: "user-form",
        "data-testid": "user-form",
        onSubmit: _cache[1] || (_cache[1] = _withModifiers((...args) => (_unref(form).handleSubmit && _unref(form).handleSubmit(...args)), ["prevent"]))
      }, [
        _createVNode(_unref(form).Field, { name: "account" }, {
          default: _withCtx(({ field }) => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("label", {
                class: "form-label",
                for: field.name
              }, _toDisplayString(isCreateWwu.value ? 'Uni-Kennung (z.B. d_muster01)' : 'Benutzername'), 9 /* TEXT, PROPS */, _hoisted_2),
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("input", {
                  id: field.name,
                  name: field.name,
                  class: _normalizeClass(["form-control", { 'is-invalid': fieldErrors(field.name).length }]),
                  value: field.state.value,
                  readonly: accountReadonly.value,
                  "data-testid": "field-account",
                  onInput: $event => (handleFieldInput(field, $event.target.value)),
                  onBlur: field.handleBlur
                }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_4),
                _createElementVNode("span", _hoisted_5, _toDisplayString(domainSuffix.value), 1 /* TEXT */)
              ]),
              _createVNode(FieldErrors, {
                errors: fieldErrors(field.name)
              }, null, 8 /* PROPS */, ["errors"])
            ])
          ]),
          _: 1 /* STABLE */
        }),
        (showIdentityFields.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (identityFieldsReadonly.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Titel, Name, Anzeigename und E-Mail werden automatisch von LDAP synchronisiert und können hier nicht bearbeitet werden. "))
                : _createCommentVNode("v-if", true),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_unref(form).Field, { name: "title" }, {
                    default: _withCtx(({ field }) => [
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("label", {
                          class: "form-label",
                          for: field.name
                        }, "Titel", 8 /* PROPS */, _hoisted_10),
                        _createElementVNode("input", {
                          id: field.name,
                          class: _normalizeClass(["form-control", { 'is-invalid': fieldErrors(field.name).length }]),
                          value: field.state.value,
                          disabled: identityFieldsReadonly.value,
                          "data-testid": "field-title",
                          onInput: $event => (handleFieldInput(field, $event.target.value))
                        }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_11),
                        _createVNode(FieldErrors, {
                          errors: fieldErrors(field.name)
                        }, null, 8 /* PROPS */, ["errors"])
                      ])
                    ]),
                    _: 1 /* STABLE */
                  })
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(_unref(form).Field, { name: "firstname" }, {
                    default: _withCtx(({ field }) => [
                      _createElementVNode("div", _hoisted_13, [
                        _createElementVNode("label", {
                          class: "form-label",
                          for: field.name
                        }, [
                          _cache[4] || (_cache[4] = _createTextVNode(" Vorname", -1 /* CACHED */)),
                          (!identityFieldsReadonly.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_15, " *"))
                            : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_14),
                        _createElementVNode("input", {
                          id: field.name,
                          class: _normalizeClass(["form-control", { 'is-invalid': fieldErrors(field.name).length }]),
                          value: field.state.value,
                          disabled: identityFieldsReadonly.value,
                          "data-testid": "field-firstname",
                          onInput: $event => (handleFieldInput(field, $event.target.value))
                        }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_16),
                        _createVNode(FieldErrors, {
                          errors: fieldErrors(field.name)
                        }, null, 8 /* PROPS */, ["errors"])
                      ])
                    ]),
                    _: 1 /* STABLE */
                  })
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createVNode(_unref(form).Field, { name: "lastname" }, {
                    default: _withCtx(({ field }) => [
                      _createElementVNode("div", _hoisted_18, [
                        _createElementVNode("label", {
                          class: "form-label",
                          for: field.name
                        }, [
                          _cache[5] || (_cache[5] = _createTextVNode(" Nachname", -1 /* CACHED */)),
                          (!identityFieldsReadonly.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_20, " *"))
                            : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_19),
                        _createElementVNode("input", {
                          id: field.name,
                          class: _normalizeClass(["form-control", { 'is-invalid': fieldErrors(field.name).length }]),
                          value: field.state.value,
                          disabled: identityFieldsReadonly.value,
                          "data-testid": "field-lastname",
                          onInput: $event => (handleFieldInput(field, $event.target.value))
                        }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_21),
                        _createVNode(FieldErrors, {
                          errors: fieldErrors(field.name)
                        }, null, 8 /* PROPS */, ["errors"])
                      ])
                    ]),
                    _: 1 /* STABLE */
                  })
                ])
              ]),
              (!identityFieldsReadonly.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    _cache[6] || (_cache[6] = _createElementVNode("label", { class: "form-label" }, "Angezeigter Name (wird automatisch erzeugt)", -1 /* CACHED */)),
                    _createElementVNode("input", {
                      class: "form-control",
                      disabled: "",
                      value: computedDisplayName.value
                    }, null, 8 /* PROPS */, _hoisted_23)
                  ]))
                : _createCommentVNode("v-if", true),
              _createVNode(_unref(form).Field, { name: "mail" }, {
                default: _withCtx(({ field }) => [
                  _createElementVNode("div", _hoisted_24, [
                    _createElementVNode("label", {
                      class: "form-label",
                      for: field.name
                    }, "E-Mail", 8 /* PROPS */, _hoisted_25),
                    _createElementVNode("input", {
                      id: field.name,
                      type: "email",
                      class: _normalizeClass(["form-control", { 'is-invalid': fieldErrors(field.name).length }]),
                      value: field.state.value,
                      disabled: identityFieldsReadonly.value,
                      "data-testid": "field-mail",
                      onInput: $event => (handleFieldInput(field, $event.target.value)),
                      onBlur: field.handleBlur
                    }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_26),
                    _createVNode(FieldErrors, {
                      errors: fieldErrors(field.name)
                    }, null, 8 /* PROPS */, ["errors"])
                  ])
                ]),
                _: 1 /* STABLE */
              }),
              (!identityFieldsReadonly.value)
                ? (_openBlock(), _createBlock(_unref(form).Field, {
                    key: 2,
                    name: "password"
                  }, {
                    default: _withCtx(({ field }) => [
                      _createElementVNode("div", _hoisted_27, [
                        _createElementVNode("label", {
                          class: "form-label",
                          for: field.name
                        }, "Passwort", 8 /* PROPS */, _hoisted_28),
                        _createElementVNode("input", {
                          id: field.name,
                          type: "password",
                          class: _normalizeClass(["form-control", { 'is-invalid': fieldErrors(field.name).length }]),
                          value: field.state.value,
                          "data-testid": "field-password",
                          onInput: $event => (handleFieldInput(field, $event.target.value)),
                          onBlur: field.handleBlur
                        }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_29),
                        _createElementVNode("small", _hoisted_30, _toDisplayString(isEdit.value
                    ? 'Leer lassen, um das bestehende Passwort beizubehalten.'
                    : 'Leer lassen für ein automatisch generiertes Passwort.'), 1 /* TEXT */),
                        _createVNode(FieldErrors, {
                          errors: fieldErrors(field.name)
                        }, null, 8 /* PROPS */, ["errors"])
                      ])
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */))
          : _createCommentVNode("v-if", true),
        _createElementVNode("div", _hoisted_31, [
          _cache[7] || (_cache[7] = _createElementVNode("label", { class: "form-label" }, "Rollen", -1 /* CACHED */)),
          _createVNode(RoleMultiSelect, {
            modelValue: selectedRoleIds.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((selectedRoleIds).value = $event)),
            roles: __props.roles
          }, null, 8 /* PROPS */, ["modelValue", "roles"])
        ])
      ], 32 /* NEED_HYDRATION */),
      (generatedPassword.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
            _cache[8] || (_cache[8] = _createTextVNode(" Das Passwort wurde automatisch generiert: ", -1 /* CACHED */)),
            _createElementVNode("strong", null, _toDisplayString(generatedPassword.value), 1 /* TEXT */)
          ]))
        : _createCommentVNode("v-if", true),
      (submitError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_33, _toDisplayString(submitError.value), 1 /* TEXT */))
        : _createCommentVNode("v-if", true)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("button", {
        type: "button",
        class: "btn btn-secondary",
        "data-testid": "user-form-cancel",
        onClick: _cache[2] || (_cache[2] = $event => (_ctx.$emit('close')))
      }, _toDisplayString(generatedPassword.value ? 'Schließen' : 'Abbrechen'), 1 /* TEXT */),
      (!generatedPassword.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "submit",
            form: "user-form",
            class: "btn btn-primary",
            "data-testid": "user-form-submit",
            disabled: isSaving.value
          }, " Speichern ", 8 /* PROPS */, _hoisted_34))
        : _createCommentVNode("v-if", true)
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show", "title"]))
}
}

}